// 📁 src/components/ProductCard.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ProductCard({
  id,
  index = 0,
  title,
  tagline,
  overview,
  features,
  message,
  image,
  currency = 'USD',
  billingCycle = 'monthly',
  onSubscribe
}) {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(billingCycle);
  const [configPrice, setConfigPrice] = useState(null);
  const [promoBoost, setPromoBoost] = useState(0);
  const [annualDiscountRate, setAnnualDiscountRate] = useState(15);
  const [exchangeRate, setExchangeRate] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopup] = useState(false); // restriction balloon
  const [popupConfig, setPopupConfig] = useState(null); // ✅ stylish alerts

  useEffect(() => {
    const hydrateConfig = () => {
      const config = JSON.parse(localStorage.getItem('adminPricingConfig')) || {};
      const promo = JSON.parse(localStorage.getItem('activePromo')) || null;

      if (config.modulePricingMap && typeof config.modulePricingMap[id] === 'number') {
        setConfigPrice(config.modulePricingMap[id]);
      }

      setAnnualDiscountRate(config.annualDiscountRate || 0);

      if (currency === 'BDT') {
        setExchangeRate(config.exchangeRate || 1);
      }

      if (promo) {
        const now = new Date();
        const from = new Date(promo.validFrom);
        const to = new Date(promo.validTo);
        if (now >= from && now <= to) {
          setPromoBoost(promo.discountRate || 0);
        }
      }

      const activeEmail =
        sessionStorage.getItem('isLoggedIn') === 'true' ? localStorage.getItem('email') : null;
      if (!activeEmail) return;

      setEmail(activeEmail);
      const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
      const account = registry[activeEmail] || {};
      const resolvedTenantId = account.tenantId || activeEmail;
      setCompanyName(account.companyName || '');
      setTenantId(resolvedTenantId);

      localStorage.setItem('tenantId', resolvedTenantId);
    };

    hydrateConfig();
    window.addEventListener('storage', hydrateConfig);
    return () => window.removeEventListener('storage', hydrateConfig);
  }, [id, currency]);

  const basePriceUSD = typeof configPrice === 'number' ? configPrice : 0;
  const isAnnual = cycle === 'annual';
  const discount = isAnnual ? (annualDiscountRate + promoBoost) / 100 : 0;

  const finalPrice = isAnnual ? basePriceUSD * 12 * (1 - discount) : basePriceUSD * (1 - discount);
  const convertedPrice = currency === 'BDT' ? finalPrice * exchangeRate : finalPrice;

  const displayPrice =
    currency === 'BDT' ? `৳${Math.round(convertedPrice)}` : `$${Math.round(convertedPrice)}`;

  const priceColors = [
    '#fff9db',
    '#ffe082',
    '#e0f7fa',
    '#80deea',
    '#fce4ec',
    '#f06292',
    '#f3e5f5',
    '#ba68c8',
    '#e8f5e9',
    '#81c784'
  ];
  const priceBgColor = priceColors[index % priceColors.length] || '#fff9db';

  const handleSubscribe = () => {
    if (!email || !tenantId) {
      navigate('/register');
      return;
    }

    const cartKey = `cartItems_${tenantId}`;
    const moduleKey = `selectedModuleId_${tenantId}`;
    const hydratedCart = JSON.parse(localStorage.getItem(cartKey) || '[]');
    const alreadyInCart = hydratedCart.some((item) => item.id === id);

    const auditLog = JSON.parse(localStorage.getItem(`paymentAuditLog_${email}`) || '[]');
    const paidHistory = auditLog.filter((entry) => entry.paymentStatus === 'paid');
    const lockedCurrency =
      paidHistory.length > 0
        ? paidHistory[0].currency
        : localStorage.getItem(`currency_${tenantId}`);

    // Currency lock warning → stylish balloon
    if (lockedCurrency && paidHistory.length > 0 && lockedCurrency !== currency) {
      setPopupConfig({
        type: 'warning',
        message: `Your account is locked to ${lockedCurrency}. Please continue with ${lockedCurrency} for all purchases.`,
        colors: 'from-yellow-500 via-orange-500 to-red-500'
      });
      return;
    }

    if (hydratedCart.length > 0 && !alreadyInCart) {
      setShowPopup(true);
      return;
    }

    if (paidHistory.length === 0 && hydratedCart.length === 0) {
      localStorage.removeItem(`currency_${tenantId}`);
    }

    if (hydratedCart.length > 0) {
      const cartCurrency = localStorage.getItem(`currency_${tenantId}`);
      if (cartCurrency && cartCurrency !== currency) {
        setPopupConfig({
          type: 'warning',
          message: `Your current cart is set to ${cartCurrency}. Please continue with ${cartCurrency} for this checkout.`,
          colors: 'from-yellow-500 via-orange-500 to-red-500'
        });
        return;
      }
    }

    if (!lockedCurrency) {
      localStorage.setItem(`currency_${tenantId}`, currency);
    }

    localStorage.setItem(moduleKey, id);
    localStorage.setItem('billingCycle', cycle);
    localStorage.setItem(`currency_${tenantId}`, currency);

    if (!alreadyInCart) {
      const newItem = {
        id,
        name: id,
        price: convertedPrice,
        quantity: 1,
        image: `/assets/modules/${id}.png`
      };
      hydratedCart.push(newItem);
      localStorage.setItem(cartKey, JSON.stringify(hydratedCart));
    }

    if (promoBoost > 0) {
      const promo = JSON.parse(localStorage.getItem('activePromo'));
      localStorage.setItem('promoApplied', 'true');
      localStorage.setItem('promoStartDate', new Date().toISOString());
      localStorage.setItem('promoDurationMonths', promo.benefitDurationMonths || '12');
      localStorage.setItem('promoName', promo.occasion || '');
      localStorage.setItem('promoDiscountRate', promo.discountRate || '0');
    }

    navigate('/cart');
  };

  return (
    <div className="product-section opacity-0 transform transition duration-700 rounded-xl shadow-md p-8 bg-white">
      {/* Restriction balloon */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-6 rounded-2xl shadow-xl max-w-sm text-center animate-bounce-once">
            <h3 className="text-lg font-bold mb-2">⚠️ One module at a time</h3>
            <p className="text-sm font-medium">
              Please complete subscribing one module first, then proceed to the next. This ensures
              each subscription has its own invoice and renewal cycle, giving you full control.
            </p>
            <button
              onClick={() => setShowPopup(false)}
              className="mt-4 bg-white text-indigo-600 px-4 py-2 rounded-full font-semibold hover:bg-gray-100"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Stylish alert balloons for currency warnings */}
      {popupConfig && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div
            className={`bg-gradient-to-r ${popupConfig.colors} text-white p-6 rounded-2xl shadow-xl max-w-sm text-center animate-bounce-once`}
          >
            <h3 className="text-lg font-bold mb-2">⚠️ Alert</h3>
            <p className="text-sm font-medium">{popupConfig.message}</p>
            <button
              onClick={() => setPopupConfig(null)}
              className="mt-4 bg-white text-indigo-600 px-4 py-2 rounded-full font-semibold hover:bg-gray-100"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8 items-stretch">
        <div className="md:w-1/2 flex flex-col justify-between space-y-4">
          <div>
            <h2 className="heading2">{title}</h2>
            <p className="text-blue-600 italic">{tagline}</p>
            <p className="para1">{overview}</p>

            {Array.isArray(features) && features.length > 0 ? (
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">Feature list unavailable</p>
            )}

            <p className="text-indigo-800 font-semibold mt-2">“{message}”</p>
          </div>

          <div
            className="w-full rounded-lg p-6 shadow-sm relative mt-4"
            style={{
              backgroundColor: priceBgColor,
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              borderRadius: '12px'
            }}
          >
            <div
              title="Switch to annual to unlock this discount"
              className={`absolute -top-4 right-4 text-xs font-bold px-3 py-1 rounded-full shadow transition ${
                isAnnual ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              Save {annualDiscountRate + promoBoost}% — Annual Only
            </div>

            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-700">Billing Cycle:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCycle('monthly');
                    localStorage.setItem('billingCycle', 'monthly');
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    cycle === 'monthly' ? 'bg-indigo-600 text-white' : 'bg-white border'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => {
                    setCycle('annual');
                    localStorage.setItem('billingCycle', 'annual');
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    cycle === 'annual' ? 'bg-indigo-600 text-white' : 'bg-white border'
                  }`}
                >
                  Annual
                </button>
              </div>
            </div>

            <div className="mb-2 text-center">
              <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
                {cycle === 'annual' ? 'Annual Billing' : 'Monthly Billing'}
              </span>
            </div>

            <div className="text-center space-y-2">
              <p className="text-2xl font-bold text-indigo-700">{displayPrice}</p>
              <p className="text-sm text-gray-700 italic">
                Designed for agile teams, fast onboarding, and real-time insights
              </p>
              <button
                onClick={handleSubscribe}
                className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 transition"
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>

        <div className="md:w-1/2 flex items-stretch justify-center">
          <img src={image} alt={title} className="w-full h-full object-contain rounded-lg shadow" />
        </div>
      </div>
    </div>
  );
}
